import { ApolloQueryResult, useQuery } from '@apollo/client';
import _isEmpty from 'lodash/isEmpty';
import unionBy from 'lodash/unionBy';
import React, { useEffect, useMemo } from 'react';
import {
  ChargeQuantity,
  EntitlementDetails,
  FetchParentEntitlementDetailsQuery,
  FetchParentEntitlementDetailsQueryVariables,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  Maybe,
  PaymentMethod,
  Promotion,
  RelationshipPathType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { UFOEventSource } from '@atlassian/temp-observability';
import { PromotionAppliedStatus } from '@atlassian/cssxp-types-promotion';
import {
  ENABLE_PARTNER_PROMOTION,
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_QUOTE,
} from '@atlassian/cssxp-utils-generic';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useApprovalDeskPermissions } from '@css-xp/apd-common';
import { AllTasksList } from '@css-xp/apd-task-manager';
import { TabPanel, Tabs, isAccess, isResourceAccess, useFeatureToggle } from '@css-xp/base';
import { currentEntitlementType, getParentContainerId } from '@css-xp/base/entitlement-utils';
import { fetchParentEntitlementDetails } from '@css-xp/base/entitlement-utils/src/queries/fetch-parent-entitlement-details';
import { QuoteTab } from '@css-xp/quotes';
import { BillingTab } from '../../../common/tabs/billing-tab';
import AppliedPromotionsTable from '../../../common/tabs/billing-tab/src/applied-promotions-table';
import { InvoiceTab } from '../../../common/tabs/invoice-tab';
import { fetchEmailsForEntitlementPage as EmailsQuery } from '../queries/fetch-entitlement-emails';
import AddOnsParentEntitlementTab from '../tabs/add-ons-tab/add-ons-parent-entitlement-tab';
import AddOnsTab from '../tabs/add-ons-tab/add-ons-tab';
import LinkedEntitlementsTab from '../tabs/app-tab/linked-entitlements-tab';
import AuditTrailTab from '../tabs/audit-trail-tab/audit-trail-tab';
import { OrderHistoryTable } from '../tabs/audit-trail-tab/order-history-table';
import { UserHistoryTable } from '../tabs/audit-trail-tab/user-history-table';
import { EmailTab } from '../tabs/email-tab/email-tab';
import ParentsEntitlementTab from '../tabs/parents-entitlement-tab';
import { PeopleTab } from '../tabs/people-tab';
import SandboxEntitlementsTab from '../tabs/sandbox-tab';
import { ScheduledBillingChangesTab } from '../tabs/schedule-tab/src/schedule-tab';
import { SupportTab } from '../tabs/support-tab/support-tab';
import { CustomerContactsTab } from '../tabs/customer-contacts-tab';

interface Props {
  statuspageEntitlement: EntitlementDetails;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
  parentProductEntitlements: LinkedDestinationEntitlement[];
  parentEntitlementsLoading: boolean;
  isPartnerAccount: boolean;
}

const EntitlementPageTabs = ({
  statuspageEntitlement,
  availablePaymentMethods,
  refetch,
  parentProductEntitlements,
  parentEntitlementsLoading,
  isPartnerAccount,
}: Props): JSX.Element | null => {
  const [isEnabled] = useFeatureToggle();
  const {
    isTrialExtApprovalFlowEnabled,
    isDiscountApprovalFlowEnabled,
    isPricingPlanChangeApprovalFlowEnabled,
  } = useApprovalDeskPermissions();
  const showApprovalsTab =
    isTrialExtApprovalFlowEnabled || isDiscountApprovalFlowEnabled || isPricingPlanChangeApprovalFlowEnabled;

  const linkedEntitlementsTabVisible = useMemo(() => {
    return (
      isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE) &&
      (!!statuspageEntitlement?.relatesFromEntitlements?.length ||
        !!statuspageEntitlement?.relatesToEntitlements?.length)
    );
  }, [statuspageEntitlement, isEnabled]);

  /**
   * GLobal Page Load experience for Emntitlement Page
   */
  GlobalPageLoadExperience.startPageLoad(UFOEventSource.ENTITLEMENT_SCREEN);
  useEffect(() => {
    GlobalPageLoadExperience.success({ force: true });
  }, []);

  const fetchLinkedParentEntitlementsCheck = () => {
    if (
      linkedEntitlementsTabVisible &&
      !statuspageEntitlement?.isSandbox &&
      !statuspageEntitlement.isApp &&
      statuspageEntitlement?.relatesToEntitlements?.length
    ) {
      const sandboxApp = statuspageEntitlement.relatesToEntitlements.filter(
        (entitlement) =>
          entitlement?.relationshipType === RelationshipPathType.SANDBOX_DEPENDENCE ||
          entitlement?.relationshipType === RelationshipPathType.SANDBOX_GRANT
      );
      return sandboxApp.length > 0;
    }
    return false;
  };
  const checkToSkipFetchLinkedParentEntitlements = fetchLinkedParentEntitlementsCheck();

  const { data: parentEntitlementDetailResponse } = useQuery<
    FetchParentEntitlementDetailsQuery,
    FetchParentEntitlementDetailsQueryVariables
  >(fetchParentEntitlementDetails, {
    variables: {
      id: statuspageEntitlement?.id,
    },
    skip: checkToSkipFetchLinkedParentEntitlements,
    displayName: 'fetchLinkedParentEntitlements',
    notifyOnNetworkStatusChange: true,
  });

  const parentEntitlementDetails = useMemo(() => {
    return parentEntitlementDetailResponse?.entitlementDetails?.parentEntitlementDetails;
  }, [parentEntitlementDetailResponse]);

  const showSandboxTab = useMemo(() => {
    if (parentEntitlementDetails?.relatesToEntitlements?.length) {
      const sandboxApp = parentEntitlementDetails.relatesToEntitlements.filter(
        (entitlement) =>
          entitlement?.relationshipType === RelationshipPathType.SANDBOX_DEPENDENCE ||
          entitlement?.relationshipType === RelationshipPathType.SANDBOX_GRANT
      );
      return sandboxApp.length > 0;
    }
    return checkToSkipFetchLinkedParentEntitlements;
  }, [parentEntitlementDetails, checkToSkipFetchLinkedParentEntitlements]);

  if (_isEmpty(statuspageEntitlement)) {
    return null;
  }

  const { parentSen } = statuspageEntitlement;

  const hasParentEntitlement = () => (parentSen ? true : false);

  const activePromotions = (statuspageEntitlement as EntitlementDetails)?.promotions || [];

  const appliedPromotions = activePromotions.map((promotion) => {
    return {
      ...promotion,
      billingCycle: PromotionAppliedStatus.CURRENT,
    };
  });

  const scheduledPromotions = (statuspageEntitlement as EntitlementDetails)?.scheduledChanges?.promotions || [];

  const nextBillingPromotions = scheduledPromotions.map((promotion) => {
    return {
      ...promotion,
      billingCycle: PromotionAppliedStatus.NEXT,
    };
  });
  const appliedPromotionsList = unionBy(appliedPromotions, nextBillingPromotions, 'promotionInstanceId');
  const parentContainerId = getParentContainerId(statuspageEntitlement);

  return (
    <Tabs className="tab-container">
      <TabPanel label="View all">
        {linkedEntitlementsTabVisible && statuspageEntitlement.isApp && !statuspageEntitlement.isSandbox && (
          <ParentsEntitlementTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={currentEntitlementType(statuspageEntitlement)}
            destinationEntitlementType={[LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT]}
            productType={statuspageEntitlement.productType}
            findApps={false}
            findProducts={true}
          />
        )}
        {linkedEntitlementsTabVisible && statuspageEntitlement?.isSandbox && statuspageEntitlement.isApp && (
          <SandboxEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT}
            destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT]}
            findApps={false}
            productType={statuspageEntitlement.productType}
            findProducts={true}
            tableHeading="Sandbox parent subscriptions"
          />
        )}
        {showSandboxTab && (
          <SandboxEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT}
            destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT]}
            findApps={false}
            productType={statuspageEntitlement.productType}
            findProducts={true}
          />
        )}

        {linkedEntitlementsTabVisible && statuspageEntitlement.isApp && !statuspageEntitlement.isSandbox && (
          <LinkedEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={currentEntitlementType(statuspageEntitlement)}
            destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT]}
            productType={statuspageEntitlement.productType}
            findApps={true}
            findProducts={false}
            baseProduct={statuspageEntitlement?.baseProduct || ''}
            tableHeading="Sandbox apps"
          />
        )}
        {linkedEntitlementsTabVisible && !statuspageEntitlement.isApp && !statuspageEntitlement.isSandbox && (
          <LinkedEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={currentEntitlementType(statuspageEntitlement)}
            destinationEntitlementType={[LinkedEntitlementTypes.PROD_ENTITLEMENT_APP]}
            productType={statuspageEntitlement.productType}
            findApps={true}
            findProducts={false}
          />
        )}
        <PeopleTab entitlement={statuspageEntitlement} />
        {isEnabled(ENABLE_PARTNER_PROMOTION) && isPartnerAccount && (
          <CustomerContactsTab
            entitlementId={statuspageEntitlement.id}
            transactionAccountId={statuspageEntitlement.transactionAccountId}
          />
        )}
        <BillingTab entitlement={statuspageEntitlement} refetch={refetch} />
        {linkedEntitlementsTabVisible && !statuspageEntitlement.isApp && statuspageEntitlement.isSandbox && (
          <LinkedEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={currentEntitlementType(statuspageEntitlement)}
            destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT]}
            productType={statuspageEntitlement.productType}
            findApps={true}
            findProducts={false}
          />
        )}
        {!hasParentEntitlement() && isStatuspage(statuspageEntitlement?.productId) && (
          <AddOnsTab entitlement={statuspageEntitlement} />
        )}
        <AppliedPromotionsTable
          entitlement={statuspageEntitlement}
          promotions={appliedPromotionsList}
          refetch={refetch}
          data-testid="entitlement-applied-promotions"
        />
        <InvoiceTab
          id={statuspageEntitlement.id}
          entitlement={statuspageEntitlement}
          availablePaymentMethods={availablePaymentMethods}
        />
        {isEnabled(ENABLE_QUOTE) && isResourceAccess(Resources.QUOTE_ALL) && isAccess(Permissions.QUOTE_MANAGE) && (
          <QuoteTab query={statuspageEntitlement.id} label="Quotes" />
        )}
        <div className="order-history-table">
          <OrderHistoryTable
            id={statuspageEntitlement.id}
            transactionAccountId={statuspageEntitlement.transactionAccountId}
            nextInvoiceTimeAt={statuspageEntitlement.nextInvoiceTimeAt}
            baseProduct={statuspageEntitlement.baseProduct}
            productKey={statuspageEntitlement.productId ?? ''}
            entitlement={statuspageEntitlement}
          />
        </div>
        <AuditTrailTab id={statuspageEntitlement.id} />
        {!isStatuspage(statuspageEntitlement?.productId ?? '') && (
          <UserHistoryTable
            id={statuspageEntitlement.id}
            createdDate={statuspageEntitlement?.createdDate}
            parentProductEntitlements={parentProductEntitlements}
            parentEntitlementsLoading={parentEntitlementsLoading}
          />
        )}
        <EmailTab id={statuspageEntitlement.id} entityName={'entitlement'} query={EmailsQuery} />
        {hasParentEntitlement() && isStatuspage(statuspageEntitlement.productId) && (
          <AddOnsParentEntitlementTab parentSen={statuspageEntitlement?.parentSen?.toString()} />
        )}
        {showApprovalsTab && (
          <AllTasksList
            businessKey={statuspageEntitlement.entitlementNumber ?? undefined}
            searchable={false}
            reloadAll={refetch}
          />
        )}
        {!!statuspageEntitlement.scheduledChanges && statuspageEntitlement.pricingPlan && (
          <ScheduledBillingChangesTab
            changeAction={statuspageEntitlement.scheduledChanges?.subscriptionScheduleAction || null}
            changeDate={statuspageEntitlement.scheduledChanges?.nextChangeTimestamp || null}
            scheduledChanges={statuspageEntitlement.scheduledChanges}
            oldValues={{
              billingFrequency: statuspageEntitlement.pricingPlan?.billingFrequency || null,
              offering: statuspageEntitlement.offering?.name || null,
              pricingPlan: statuspageEntitlement.pricingPlan?.type,
            }}
            newValues={{
              billingFrequency: statuspageEntitlement.scheduledChanges?.pricingPlan?.billingFrequency || null,
              offering: statuspageEntitlement.scheduledChanges?.offering?.name || null,
              pricingPlan: statuspageEntitlement.scheduledChanges?.pricingPlan?.type || null,
            }}
            scheduledPromotions={(statuspageEntitlement.scheduledChanges?.promotions ?? []) as Promotion[]}
            entitlementNumber={statuspageEntitlement.entitlementNumber}
            entitlementId={statuspageEntitlement.id}
            productId={statuspageEntitlement.productId}
            productName={statuspageEntitlement.productName}
            units={statuspageEntitlement.unitCount}
            refetch={refetch}
            promotions={(statuspageEntitlement?.promotions ?? []) as Promotion[]}
            scheduledQuantity={(statuspageEntitlement.scheduledChanges?.chargeQuantities ?? []) as ChargeQuantity[]}
            entitlement={statuspageEntitlement}
            parentProductEntitlements={parentProductEntitlements}
          />
        )}
      </TabPanel>
      {linkedEntitlementsTabVisible && statuspageEntitlement.isApp && !statuspageEntitlement.isSandbox && (
        <TabPanel label="Parents" enabled={true}>
          <ParentsEntitlementTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={currentEntitlementType(statuspageEntitlement)}
            destinationEntitlementType={[LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT]}
            productType={statuspageEntitlement.productType}
            findApps={false}
            findProducts={true}
          />
        </TabPanel>
      )}
      {linkedEntitlementsTabVisible && statuspageEntitlement?.isSandbox && statuspageEntitlement.isApp && (
        <TabPanel label="Parents" enabled={true}>
          <SandboxEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT}
            destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT]}
            findApps={false}
            productType={statuspageEntitlement.productType} // TODO: need to change based on product type
            findProducts={true}
            tableHeading="Sandbox parent subscriptions"
          />
        </TabPanel>
      )}
      {linkedEntitlementsTabVisible && !statuspageEntitlement.isApp && !statuspageEntitlement.isSandbox && (
        <TabPanel label={'Apps'}>
          <LinkedEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={currentEntitlementType(statuspageEntitlement)}
            destinationEntitlementType={[LinkedEntitlementTypes.PROD_ENTITLEMENT_APP]}
            productType={statuspageEntitlement.productType}
            findApps={true}
            findProducts={false}
          />
        </TabPanel>
      )}
      {showSandboxTab && (
        <TabPanel label="Sandbox" enabled={true}>
          <SandboxEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT}
            destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT]}
            findApps={false}
            productType={statuspageEntitlement.productType} // TODO: need to change based on product type
            findProducts={true}
          />

          {linkedEntitlementsTabVisible && statuspageEntitlement.isApp && !statuspageEntitlement.isSandbox && (
            <LinkedEntitlementsTab
              id={parentContainerId}
              entitlementId={statuspageEntitlement.id}
              currentEntitlementType={currentEntitlementType(statuspageEntitlement)}
              destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT]}
              productType={statuspageEntitlement.productType}
              findApps={true}
              findProducts={false}
              baseProduct={statuspageEntitlement?.baseProduct || ''}
              tableHeading="Sandbox apps"
            />
          )}
        </TabPanel>
      )}

      <TabPanel label="Contacts" enabled={true}>
        <PeopleTab entitlement={statuspageEntitlement} />
      </TabPanel>
      {isEnabled(ENABLE_PARTNER_PROMOTION) && isPartnerAccount && (
        <TabPanel label="Customer Contacts" enabled={true}>
          <CustomerContactsTab
            entitlementId={statuspageEntitlement.id}
            transactionAccountId={statuspageEntitlement.transactionAccountId}
          />
        </TabPanel>
      )}
      <TabPanel label="Billing" enabled={true}>
        <BillingTab entitlement={statuspageEntitlement} refetch={refetch} />
      </TabPanel>
      {linkedEntitlementsTabVisible && !statuspageEntitlement.isApp && statuspageEntitlement.isSandbox && (
        <TabPanel label={'Apps'}>
          <LinkedEntitlementsTab
            id={parentContainerId}
            entitlementId={statuspageEntitlement.id}
            currentEntitlementType={currentEntitlementType(statuspageEntitlement)}
            destinationEntitlementType={[LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT]}
            productType={statuspageEntitlement.productType}
            findApps={true}
            findProducts={false}
          />
        </TabPanel>
      )}
      {!hasParentEntitlement() && isStatuspage(statuspageEntitlement?.productId) && (
        <TabPanel label="Additional allocations" enabled={true}>
          <AddOnsTab entitlement={statuspageEntitlement} />
        </TabPanel>
      )}
      <TabPanel label="Discounts" enabled={true}>
        <AppliedPromotionsTable
          entitlement={statuspageEntitlement}
          promotions={appliedPromotionsList}
          refetch={refetch}
          data-testid="entitlement-applied-promotions"
        />
      </TabPanel>
      <TabPanel label="Invoices" enabled={true}>
        <InvoiceTab
          id={statuspageEntitlement.id}
          entitlement={statuspageEntitlement}
          availablePaymentMethods={availablePaymentMethods}
        />
      </TabPanel>
      <TabPanel label="Order history" enabled={true}>
        <OrderHistoryTable
          id={statuspageEntitlement.id}
          transactionAccountId={statuspageEntitlement.transactionAccountId}
          nextInvoiceTimeAt={statuspageEntitlement.nextInvoiceTimeAt}
          baseProduct={statuspageEntitlement.baseProduct}
          productKey={statuspageEntitlement.productId ?? ''}
          entitlement={statuspageEntitlement}
        />
      </TabPanel>
      <TabPanel label="Audit trail" enabled={true}>
        <AuditTrailTab id={statuspageEntitlement.id} />
      </TabPanel>
      {!isStatuspage(statuspageEntitlement?.productId ?? '') && (
        <TabPanel label="User history">
          <UserHistoryTable
            id={statuspageEntitlement.id}
            createdDate={statuspageEntitlement?.createdDate}
            parentProductEntitlements={parentProductEntitlements}
            parentEntitlementsLoading={parentEntitlementsLoading}
          />
        </TabPanel>
      )}
      <TabPanel label="Emails" enabled={true}>
        <EmailTab id={statuspageEntitlement.id} entityName={'entitlement'} query={EmailsQuery} />
      </TabPanel>
      <TabPanel label="Support tickets" enabled={true}>
        <SupportTab id={statuspageEntitlement.id} />
      </TabPanel>
      {hasParentEntitlement() && isStatuspage(statuspageEntitlement.productId) && (
        <TabPanel label="Parent subscription" enabled={true}>
          <AddOnsParentEntitlementTab parentSen={statuspageEntitlement?.parentSen?.toString()} />
        </TabPanel>
      )}
      {showApprovalsTab ? (
        <TabPanel label="Approvals" enabled={true}>
          <AllTasksList
            businessKey={statuspageEntitlement.entitlementNumber ?? undefined}
            searchable={false}
            reloadAll={refetch}
          />
        </TabPanel>
      ) : null}
      <TabPanel label="Scheduled changes" enabled={!!statuspageEntitlement.scheduledChanges}>
        <ScheduledBillingChangesTab
          changeAction={statuspageEntitlement.scheduledChanges?.subscriptionScheduleAction || null}
          changeDate={statuspageEntitlement.scheduledChanges?.nextChangeTimestamp || null}
          scheduledChanges={statuspageEntitlement.scheduledChanges}
          oldValues={{
            billingFrequency: statuspageEntitlement.pricingPlan?.billingFrequency || null,
            offering: statuspageEntitlement.offering?.name || null,
            pricingPlan: statuspageEntitlement.pricingPlan?.type,
          }}
          newValues={{
            billingFrequency: statuspageEntitlement.scheduledChanges?.pricingPlan?.billingFrequency || null,
            offering: statuspageEntitlement.scheduledChanges?.offering?.name || null,
            pricingPlan: statuspageEntitlement.scheduledChanges?.pricingPlan?.type || null,
          }}
          scheduledPromotions={(statuspageEntitlement.scheduledChanges?.promotions ?? []) as Promotion[]}
          entitlementNumber={statuspageEntitlement.entitlementNumber}
          entitlementId={statuspageEntitlement.id}
          productId={statuspageEntitlement.productId}
          productName={statuspageEntitlement.productName}
          units={statuspageEntitlement.unitCount}
          refetch={refetch}
          promotions={(statuspageEntitlement?.promotions ?? []) as Promotion[]}
          scheduledQuantity={(statuspageEntitlement.scheduledChanges?.chargeQuantities ?? []) as ChargeQuantity[]}
          entitlement={statuspageEntitlement}
          parentProductEntitlements={parentProductEntitlements}
        />
      </TabPanel>
      {isEnabled(ENABLE_QUOTE) && isResourceAccess(Resources.QUOTE_ALL) && isAccess(Permissions.QUOTE_MANAGE) && (
        <TabPanel label="Quote">
          <QuoteTab query={statuspageEntitlement.id} label="Quotes" />
        </TabPanel>
      )}
    </Tabs>
  );
};

export default EntitlementPageTabs;
